import { CompactBand, BandTab, BandMintLink, BandSep } from '@/components/CompactBand';
import type { Settings, Tab } from '../types';
import { OWNER_NAME, money } from '../utils';

interface HeaderProps {
  view: Tab;
  /** True on the Pre-auths › Closed history view (?tab=closed). */
  closed?: boolean;
  settings: Settings;
  toSendCount: number;
  toSendTotal: number;
  needsCount: number;
  /** To-send items waiting for an X-ray someone chose to add; they are not "ready" and don't need a person. */
  waitingCount?: number;
  waitingTotal?: number;
  sentCount: number;
  approvedCount: number;
  tabTo: (view: Tab | 'closed') => string;
}

function headline(toSendCount: number, toSendTotal: number, needsCount: number, waitingCount: number, waitingTotal: number): React.ReactNode {
  if (toSendCount === 0) {
    return 'No pre-auths are waiting to be sent.';
  }
  const actionable = toSendCount - waitingCount;
  const waiting = waitingCount > 0 ? ` ${waitingCount === 1 ? 'One is' : `${String(waitingCount)} are`} waiting for an X-ray.` : '';
  if (actionable === 0) {
    return `No pre-auths are ready to send.${waiting}`;
  }
  const lead = `${String(actionable)} ${actionable === 1 ? 'pre-auth is' : 'pre-auths are'} ready to send, ${money(toSendTotal - waitingTotal)}.`;
  if (needsCount === 0) {
    return `${lead}${waiting}`;
  }
  const need = needsCount === 1 ? 'One needs you.' : `${String(needsCount)} need you.`;
  return (
    <>
      {lead} <em>{need}</em>
      {waiting}
    </>
  );
}

export function Header({ view, closed = false, settings, toSendCount, toSendTotal, needsCount, waitingCount = 0, waitingTotal = 0, sentCount, approvedCount, tabTo }: HeaderProps) {
  const on = settings.autopilot;
  return (
    <CompactBand
      headline={headline(toSendCount, toSendTotal, needsCount, waitingCount, waitingTotal)}
      right={
        <>
          <span>
            {on
              ? `Autopilot on · since ${settings.autopilotSince ?? 'Oct 1'} · turned on by ${settings.autopilotBy ?? OWNER_NAME}`
              : 'Autopilot off'}
          </span>
          <BandMintLink to="/settings/autopilot">Change</BandMintLink>
          <BandSep />
          <BandMintLink to="/settings/pre-auths">Pre-auth settings</BandMintLink>
        </>
      }
      tabs={
        <>
          <BandTab
            label="To send"
            meta={`${String(toSendCount)} · ${money(toSendTotal)}`}
            active={!closed && view === 'to_send'}
            to={tabTo('to_send')}
          />
          <BandTab
            label="Sent"
            meta={sentCount}
            active={!closed && view === 'sent'}
            to={tabTo('sent')}
          />
          <BandTab
            label="Approved"
            meta={approvedCount}
            active={!closed && view === 'approved'}
            to={tabTo('approved')}
          />
          <BandTab
            label="Closed"
            active={closed}
            to={tabTo('closed')}
          />
        </>
      }
    />
  );
}
